enum AvcConnectStep {
  Intro = 'intro',
  Error = 'error',
}

enum AvcCallbackResult {
  Connected = 'connected',
  Error = 'error',
}

enum AvcAuthErrorReason {
  UserDenied = 'user_denied',
  StateInvalid = 'state_invalid',
  StateExpired = 'state_expired',
  CodeExchangeFailed = 'code_exchange_failed',
  InternalError = 'internal_error',
  OrgAdminRequired = 'org_admin_required',
  RetailerRegionUnsupported = 'retailer_region_unsupported',
  Unknown = 'unknown',
}

/**
 * `error_code` values `POST /v1/integrations/amazon-vendor/auth/init` can return,
 * mapped onto the reasons the modal knows how to explain.
 */
const AVC_AUTH_INIT_ERROR_CODES: Record<string, AvcAuthErrorReason> = {
  ORG_ADMIN_REQUIRED: AvcAuthErrorReason.OrgAdminRequired,
  RETAILER_REGION_UNSUPPORTED: AvcAuthErrorReason.RetailerRegionUnsupported,
};

function toAvcAuthInitErrorReason(errorCode: string | undefined): AvcAuthErrorReason {
  return (errorCode && AVC_AUTH_INIT_ERROR_CODES[errorCode]) || AvcAuthErrorReason.Unknown;
}

type AvcCallback =
  | { result: AvcCallbackResult.Connected; integrationId?: string }
  | { result: AvcCallbackResult.Error; reason: AvcAuthErrorReason };

type AvcCallbackSearch = {
  avc?: AvcCallbackResult;
  id?: string;
  reason?: AvcAuthErrorReason;
};

const AVC_CALLBACK_RESULTS = Object.values(AvcCallbackResult) as readonly string[];

/** The five reasons the auth callback (BLU-67) can redirect back with; the rest are raised by auth-init itself. */
const AVC_AUTH_ERROR_REASONS: readonly string[] = [
  AvcAuthErrorReason.UserDenied,
  AvcAuthErrorReason.StateInvalid,
  AvcAuthErrorReason.StateExpired,
  AvcAuthErrorReason.CodeExchangeFailed,
  AvcAuthErrorReason.InternalError,
];

function isAvcCallbackResult(value: unknown): value is AvcCallbackResult {
  return typeof value === 'string' && AVC_CALLBACK_RESULTS.includes(value);
}

function isAvcAuthErrorReason(value: unknown): value is AvcAuthErrorReason {
  return typeof value === 'string' && AVC_AUTH_ERROR_REASONS.includes(value);
}

function parseAvcCallbackSearch(search: Record<string, unknown>): AvcCallbackSearch {
  const result: AvcCallbackSearch = {};
  if (isAvcCallbackResult(search.avc)) result.avc = search.avc;
  if (typeof search.id === 'string') result.id = search.id;
  if (isAvcAuthErrorReason(search.reason)) result.reason = search.reason;
  return result;
}

function toAvcCallback(search: AvcCallbackSearch): AvcCallback | undefined {
  if (search.avc === AvcCallbackResult.Connected) {
    return { result: AvcCallbackResult.Connected, integrationId: search.id };
  }
  if (search.avc === AvcCallbackResult.Error) {
    return { result: AvcCallbackResult.Error, reason: search.reason ?? AvcAuthErrorReason.Unknown };
  }
  return undefined;
}

export {
  AvcAuthErrorReason,
  AvcCallbackResult,
  AvcConnectStep,
  isAvcAuthErrorReason,
  isAvcCallbackResult,
  parseAvcCallbackSearch,
  toAvcAuthInitErrorReason,
  toAvcCallback,
};
export type { AvcCallback, AvcCallbackSearch };
